import { Box, IconButton, Typography } from '@mui/material';
import styled, { css } from 'styled-components';

import { BaseColorKey, NotifierColorKey, SubjectColorKey } from '../../theme';

interface ColorProps {
  color: BaseColorKey | SubjectColorKey | NotifierColorKey;
}

// A window this short leaves a scrolling dialog's content little room beside its title,
// description and buttons
const SHORT_WINDOW = '34em';

interface ContainerProps {
  $bare?: boolean;
  $width?: number;
  $scrollY?: boolean;
}

// `$bare` strips the surface so self-contained content (an embedded page, an
// image) sits straight on the backdrop and supplies its own edges.
const Container = styled(Box)<ContainerProps>`
  && {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: ${({ $width }) => ($width !== undefined ? `${$width}px` : 'auto')};
    min-width: ${({ $bare }) => ($bare ? 'auto' : 'min(400px, 95%)')};
    max-width: 95%;
    background-color: ${({ theme, $bare }) =>
      $bare ? 'transparent' : theme.palette.backdrop.light};
    box-shadow: ${({ theme, $bare }) => ($bare ? 'none' : theme.shadows[24])};
    padding: ${({ theme, $bare }) => ($bare ? 0 : theme.spacing(3))};
    border-radius: ${({ theme }) => theme.shape.borderRadius}px;
    outline: none;
    /* The modal root does not scroll, so a scrolling dialog stops at the window's
       edges and its content gives up the height. In a short window the dialog
       scrolls as a whole instead. */
    ${({ $scrollY }) =>
      $scrollY &&
      css`
        display: flex;
        flex-direction: column;
        max-height: 100%;
        overflow-y: auto;
      `}
  }
`;

const CloseIconButton = styled(IconButton)`
  && {
    position: absolute;
    right: ${({ theme }) => theme.spacing(2)};
    top: ${({ theme }) => theme.spacing(2)};
  }
`;

const TitleContainer = styled.div<ColorProps>`
  color: ${({ theme, color }) => theme.palette[color].dark};
`;

const Title = styled(Typography)``;

interface DescriptionProps {
  $scrollY?: boolean;
}

const Description = styled(Typography).attrs({
  component: 'div',
})<DescriptionProps>`
  && {
    margin-top: ${({ theme }) => theme.spacing(2)};
    /* In a column the paragraphs' 1em margins stay inside this box rather than
       collapsing into the 16px gaps around it, so its own margins make up the
       difference and each gap stays max(16px, 1em), as in block layout. */
    ${({ theme, $scrollY }) =>
      $scrollY &&
      css`
        margin-top: max(0px, ${theme.spacing(2)} - 1em);
        margin-bottom: max(-${theme.spacing(2)}, -1em);
      `}
  }
`;

const ButtonBar = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

interface ChildBoxProps {
  $scrollY?: boolean;
  $bare?: boolean;
}

const ChildBox = styled.div<ChildBoxProps>`
  margin-top: ${({ theme, $bare }) => ($bare ? 0 : theme.spacing(2))};
  max-height: ${({ $scrollY }) => ($scrollY ? '80vh' : 'none')};
  /* auto, not scroll, so the native track only appears when content actually
     exceeds the max-height — no empty track on short modals. */
  overflow-y: ${({ $scrollY }) => ($scrollY ? 'auto' : 'visible')};

  ${({ $scrollY }) =>
    $scrollY &&
    css`
      @media (max-height: ${SHORT_WINDOW}) {
        max-height: none;
        overflow-y: visible;
      }
    `}
`;

export { ButtonBar, ChildBox, CloseIconButton, Container, Description, Title, TitleContainer };
